import * as React from 'react';
import { Dispatch } from 'react-redux';

import { StoreState } from '../../../reducers';
import { setActiveItem, resetActiveItem, updateEditableButton, toggleEditPanel } from '../../../containers/Editor/actions';
import EditPanel from '../../../containers/Editor/EditPanel';
import ThemesPanel from '../../../containers/Editor/EditPanel/ThemesPanel';
import LinkPanel from '../../../containers/Editor/EditPanel/LinkPanel';
import ActionsPanel from '../../../containers/Editor/ActionsPanel';
import { ActionType, ActionData } from '../ActionsPanel';
import { getBorderWidth, isElementOverlapped, isElementHidden, LinkData } from '../../../containers/Editor/utils/helpers';
import { Wrapper, EditorToggle, Border } from './styles';
import { EditableType, IGNORE_PAGE_CLICK_CLASSNAME } from '../../../containers/Editor/constants';
import constants from '../../../containers/Editor/utils/indesignEditor/constants';

interface ButtonEditorProps {
	page: number;
	id: string;
	isActive: boolean;
	isVisible: boolean;
	pageScale: number;
	parentContainer: HTMLElement;
	isEditPanelDisplayed: boolean;
	isThemedItem: boolean;
	hasLink: boolean;
	linkData: LinkData;
	inDesignAction?: string;
	isLocked?: boolean;
	dispatch: Dispatch<StoreState>;
}

enum ButtonState {
	NORMAL = 'NORMAL',
	HOVER = 'HOVER',
	CLICK = 'CLICK',
}

const STATE_CLASSNAME_MAP = new Map<ButtonState, string>([
	[ButtonState.NORMAL, `${constants.buttonStateClassName}-Normal`],
	[ButtonState.HOVER, `${constants.buttonStateClassName}-Rollover`],
	[ButtonState.CLICK, `${constants.buttonStateClassName}-Click`],
]);

const ButtonEditor = ({
	page,
	id,
	isActive,
	isVisible,
	pageScale,
	parentContainer,
	isEditPanelDisplayed,
	isThemedItem,
	hasLink,
	linkData,
	inDesignAction,
	isLocked,
	dispatch,
}: ButtonEditorProps) => {
	const buttonStateElements = React.useRef<HTMLDivElement[]>([]);
	const [states, setStates] = React.useState<ButtonState[]>([]);
	const [activeState, setActiveState] = React.useState(ButtonState.NORMAL);

	const handleClick = () => {
		if (!isThemedItem && !hasLink) {
			return;
		}

		if (!isActive) {
			dispatch(setActiveItem(id, page, null));

			if (!isEditPanelDisplayed) {
				dispatch(toggleEditPanel());
			}
		}
	};

	const handleActionsPanelClick = () => {
		if (!isActive) {
			dispatch(setActiveItem(id, page, null));
		}
	};

	const handleChange = (linkData: LinkData) => {
		dispatch(updateEditableButton(page, id, {linkData}));
	};

	const renderEditPanel = () => {
		const editPanels = {};

		if (hasLink) {
			editPanels[EditableType.link] = (
				<LinkPanel
					linkData={linkData}
					canAddPopupAction={true}
					onChange={handleChange}
				/>
			);
		}

		if (isThemedItem) {
			editPanels[EditableType.themes] = <ThemesPanel />;
		}

		return (hasLink || isThemedItem) && isActive && isEditPanelDisplayed && (
			<EditPanel
				defaultType={hasLink ? EditableType.link : EditableType.themes}
				panels={editPanels}
			/>
		);
	};

	const getActions = (): ActionData[] => {
		let actions: ActionData[] = [];

		if (!isLocked) {
			actions.push({
				type: ActionType.TOGGLE_VISIBILITY,
				onClick: () => {
					dispatch(updateEditableButton(page, id, {
						isVisible: !isVisible,
					}));
				},
				isInverted: !isVisible,
			});

			if (hasLink) {
				actions.push({
					type: ActionType.EDIT_LINK,
					onClick: () => {
						dispatch(toggleEditPanel());
					},
					isDisabled: !isVisible,
				});
			}

			if (states.length > 1) {
				actions.push({
					type: ActionType.TOGGLE_BUTTON_STATE,
					onClick: () => {
						const currentStateIndex = states.indexOf(activeState);
						const isLastState = currentStateIndex === states.length - 1;
						const nextState = isLastState ? states[0] : states[currentStateIndex + 1];
						setActiveState(nextState);
					},
					isDisabled: !isVisible,
				});
			}
		}

		if (inDesignAction) {
			actions.push({
				type: ActionType.RUN_INDESIGN_ACTION,
				onClick: () => {
					if (!isVisible) {
						return;
					}

					new Function(inDesignAction)();

					// tslint:disable-next-line: max-line-length
					// Timeout is needed to give inDesign scripts time to apply changes to DOM (change classes). MutationObserver on button can't be used because in some cases inDesign scripts change class on button's parent element (e.g., add class to hide it and its content) or on another element which is located above or below button in DOM (e.g., add class to show hidden content)
					const DELAY_TO_HIDE = 300;
					setTimeout(() => {
						if (isElementOverlapped(parentContainer) || isElementHidden(parentContainer)) {
							dispatch(resetActiveItem());
						}
					}, DELAY_TO_HIDE);
				},
				isDisabled: !isVisible,
			});
		}

		return actions;
	};

	React.useEffect(() => {
		if (isVisible) {
			parentContainer.removeAttribute('data-hidden');
		} else {
			parentContainer.setAttribute('data-hidden', 'true');
		}
	}, [isVisible]);

	React.useEffect(() => {
		// do not cut actions button
		parentContainer.style.overflow = 'visible';
	}, []);

	React.useEffect(() => {
		buttonStateElements.current = [...parentContainer.querySelectorAll(`[class*=${constants.buttonStateClassName}]`)] as HTMLDivElement[];

		if (buttonStateElements.current.length < 2) {
			return;
		}

		const states: ButtonState[] = [];

		for (const [state, className] of STATE_CLASSNAME_MAP) {
			const buttonStateElement = buttonStateElements.current.find(({ classList }) => classList.contains(className));

			if (buttonStateElement) {
				states.push(state);
			}
		}

		setStates(states);
		setActiveState(states[0]);
	}, []);

	React.useEffect(() => {
		if (states.length < 2) {
			return;
		}

		const activeStateClassName = STATE_CLASSNAME_MAP.get(activeState) as string;
		buttonStateElements.current.forEach((stateElement) => {
			const visibilityStyle = stateElement.classList.contains(activeStateClassName)
				? 'visible'
				: 'hidden';
			stateElement.style.visibility = visibilityStyle;
		});
	}, [states, activeState]);

	const actions = getActions();

	return (
		<>
			<Wrapper className={IGNORE_PAGE_CLICK_CLASSNAME}>
				<EditorToggle
					isVisible={isVisible}
					onClick={handleClick}
					onTouchEnd={handleClick}
				/>
				{(isActive || !isVisible) &&
					<Border
						borderWidth={getBorderWidth(pageScale)}
						isActive={isActive}
					/>
				}
				{actions.length > 0 &&
					<ActionsPanel
						actions={actions}
						pageScale={pageScale}
						onToggle={handleActionsPanelClick}
					/>
				}
			</Wrapper>
			{renderEditPanel()}
		</>
	);
};

export default ButtonEditor;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/Editor/ButtonEditor/index.tsx